import { Component, Input, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Address } from "src/app/shared/Models/Address";
import { AddressTypeEnum } from "src/app/shared/Models/enums/AddressTypeEnum";
import { BuildingTypeEnum } from "src/app/shared/Models/enums/BuildingTypeEnum";
import { Building, Survey } from "src/app/shared/Models/read";

export const addressTypeKey: string = "addressType";
export const studentHomeCountKey: string = "studentHomeCount";
export const buildingTypeKey: string = "buildingType";
export const numberOfFloorsKey: string = "numberOfFloors";

export const floorQuestions = [
  { key: "numberOfFloors", quantity: 0, label: "Floors" },
];
export const unitQuestions = [
  { key: "numberOfLivingUnits", quantity: 0, label: "Lu", originalValue: 0 },
  { key: "numberOfBusinessUnits", quantity: 0, label: "Bu", originalValue: 0 },
  { key: "numberOfServiceUnits", quantity: 0, label: "Su", originalValue: 0 },
];

@Component({
  selector: "app-outside-units",
  template: ``,
})
export class OutsideUnitBaseComponent implements OnDestroy {
  buildingTypes = Object.keys(BuildingTypeEnum).filter((s) => !isNaN(+s));
  addressTypeTexts = {};
  unitForms: { [key: string]: FormGroup } = {};
  studentHomeCountKey = studentHomeCountKey;
  addressTypeKey = addressTypeKey;

  buildingTypeEnum = BuildingTypeEnum;
  addressTypeEnum = AddressTypeEnum;

  unitQuestions = unitQuestions;
  floorQuestions = floorQuestions;
  @Input() isAuthCanWrite: boolean;
  @Input() survey: Survey;

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  constructor() {}
  init(buildings: Building[]) {
    buildings.forEach((b) => {
      if (b.buildingType === null) b.buildingType = -1;

      var form = new FormGroup({});
      b.addresses.forEach((address, index) => {
        form.addControl(
          addressTypeKey + index,
          new FormControl(address.addressType, [Validators.required])
        );
      });

      form.addControl(
        buildingTypeKey,
        new FormControl(b.buildingType, [Validators.required])
      );
      if (b.outsideSurvey) {
        form.addControl(
          "numberOfFloors",
          new FormControl(b.outsideSurvey.numberOfFloors)
        );
        form.addControl(
          "numberOfLivingUnits",
          new FormControl(b.outsideSurvey.numberOfLivingUnits)
        );
        form.addControl(
          "numberOfServiceUnits",
          new FormControl(b.outsideSurvey.numberOfServiceUnits)
        );
        form.addControl(
          "numberOfBusinessUnits",
          new FormControl(b.outsideSurvey.numberOfBusinessUnits)
        );
        form.addControl("checkbox", new FormControl(b.outsideSurvey.isMDU));
      }
      this.unitForms[b.id] = form;
      this.unitForms[b.id]
        .get(buildingTypeKey)
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((typeChange) => {
          this.setAdressTypePossibilities(b, +typeChange);
          this.toggleStudentHomeCountForBuilding(
            typeChange == this.buildingTypeEnum.StudentHome,
            b
          );
          b.addresses.forEach((a) => {
            this.toggleStudentHomeCountForAddress(
              typeChange == this.buildingTypeEnum.StudentHome,
              b,
              a
            );
          });
        });

      this.toggleStudentHomeCountForBuilding(
        b.buildingType == BuildingTypeEnum.StudentHome,
        b
      );

      b.addresses.forEach((address, index) => {
        this.toggleStudentHomeCountForAddress(
          address.addressType == AddressTypeEnum.StudentHome,
          b,
          address
        );
        this.unitForms[b.id]
          .get(addressTypeKey + index)
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((value) =>
            this.toggleStudentHomeCountForAddress(
              value == AddressTypeEnum.StudentHome,
              b,
              address
            )
          );
      });
    });
  }

  toggleStudentHomeCountForBuilding(b: boolean, building: Building) {
    let control = new FormControl(building.studentHomeCount || 0, [
      Validators.required,
    ]);
    if (b)
      this.unitForms[building.id].addControl(
        "building" + studentHomeCountKey,
        control
      );
    else
      this.unitForms[building.id].removeControl(
        "building" + studentHomeCountKey
      );
  }

  toggleStudentHomeCountForAddress(
    b: boolean,
    building: Building,
    address: Address
  ) {
    let control = new FormControl(address.studentHomeCount || 0, [
      Validators.required,
    ]);
    if (b)
      this.unitForms[building.id].addControl(
        "address" + address.id + studentHomeCountKey,
        control
      );
    else
      this.unitForms[building.id].removeControl(
        "address" + address.id + studentHomeCountKey
      );
  }

  get addressTypes() {
    return Object.keys(AddressTypeEnum).filter((s) => !isNaN(+s));
  }

  calculateTotalUnits() {
    return this.unitQuestions.reduce((a, i) => a + i.quantity, 0);
  }

  calculateTotalUnitsForBuilding(building: Building) {
    return (
      building.outsideSurvey.numberOfServiceUnits +
      building.outsideSurvey.numberOfLivingUnits +
      building.outsideSurvey.numberOfBusinessUnits
    );
  }

  getAdressTypePossibilities(b: Building, buildingType: BuildingTypeEnum) {
    if (this.calculateTotalUnits() > 1)
      switch (+buildingType) {
        case BuildingTypeEnum.InUse:
          b.addresses.forEach((a, i) => {
            let addressValue = this.unitForms[b.id]?.get([
              addressTypeKey + i,
            ]).value;
            if (
              !(
                addressValue == AddressTypeEnum.InUseOneUnit ||
                addressValue == AddressTypeEnum.NotInUse ||
                addressValue == AddressTypeEnum.StudentHome ||
                addressValue == AddressTypeEnum.InUseMultipleUnits
              )
            ) {
              this.unitForms[b.id]?.patchValue({
                [addressTypeKey + i]: AddressTypeEnum.InUseMultipleUnits,
              });
              this.unitForms[b.id]?.markAsDirty();
            }
          });
          return [
            AddressTypeEnum.InUseMultipleUnits,
            AddressTypeEnum.InUseOneUnit,
            AddressTypeEnum.NotInUse,
            AddressTypeEnum.StudentHome,
          ];
        case BuildingTypeEnum.NotInUse:
          b.addresses.forEach((a, i) =>
            this.unitForms[b.id]?.patchValue({
              [addressTypeKey + i]: AddressTypeEnum.NotInUse,
            })
          );
          return [AddressTypeEnum.NotInUse];
        case BuildingTypeEnum.EmptyParcel:
          b.addresses.forEach((a, i) =>
            this.unitForms[b.id]?.patchValue({
              [addressTypeKey + i]: AddressTypeEnum.EmptyParcel,
            })
          );
          return [AddressTypeEnum.EmptyParcel];
        case BuildingTypeEnum.NoAccess:
          b.addresses.forEach((a, i) => {
            let addressValue = this.unitForms[b.id]?.get([
              addressTypeKey + i,
            ]).value;
            if (
              !(
                addressValue == AddressTypeEnum.InUseOneUnit ||
                addressValue == AddressTypeEnum.NotInUse ||
                addressValue == AddressTypeEnum.InUseMultipleUnits
              )
            ) {
              this.unitForms[b.id]?.patchValue({
                [addressTypeKey + i]: AddressTypeEnum.InUseMultipleUnits,
              });
              this.unitForms[b.id]?.markAsDirty();
            }
          });
          return [
            AddressTypeEnum.InUseMultipleUnits,
            AddressTypeEnum.InUseOneUnit,
            AddressTypeEnum.NotInUse,
          ];
        case BuildingTypeEnum.StudentHome:
          b.addresses.forEach((a, i) =>
            this.unitForms[b.id]?.patchValue({
              [addressTypeKey + i]: AddressTypeEnum.StudentHome,
            })
          );
          return [AddressTypeEnum.StudentHome];
        case BuildingTypeEnum.InUseWithoutBuildingUnit:
          b.addresses.forEach((a, i) => {
            let addressValue = this.unitForms[b.id]?.get([
              addressTypeKey + i,
            ]).value;
            if (
              !(
                addressValue == AddressTypeEnum.InUseZeroUnits ||
                addressValue == AddressTypeEnum.NotInUse
              )
            ) {
              this.unitForms[b.id]?.patchValue({
                [addressTypeKey + i]: AddressTypeEnum.InUseZeroUnits,
              });
              this.unitForms[b.id]?.markAsDirty();
            }
          });
          return [AddressTypeEnum.InUseZeroUnits, AddressTypeEnum.NotInUse];
        default:
          b.addresses.forEach((a, i) => {
            let addressValue = this.unitForms[b.id]?.get([
              addressTypeKey + i,
            ]).value;
            if (addressValue == AddressTypeEnum.NA) {
              this.unitForms[b.id]?.patchValue({
                [addressTypeKey + i]: AddressTypeEnum.InUseMultipleUnits,
              });
              this.unitForms[b.id]?.markAsDirty();
            }
          });
          return [
            AddressTypeEnum.InUseMultipleUnits,
            AddressTypeEnum.InUseOneUnit,
            AddressTypeEnum.NotInUse,
            AddressTypeEnum.StudentHome,
          ];
      }
    else
      switch (+buildingType) {
        case BuildingTypeEnum.InUse:
          b.addresses.forEach((a, i) => {
            let addressValue = this.unitForms[b.id]?.get([
              addressTypeKey + i,
            ]).value;
            if (
              !(
                addressValue == AddressTypeEnum.InUseOneUnit ||
                addressValue == AddressTypeEnum.NotInUse ||
                addressValue == AddressTypeEnum.StudentHome
              )
            ) {
              this.unitForms[b.id]?.patchValue({
                [addressTypeKey + i]: AddressTypeEnum.InUseOneUnit,
              });
              this.unitForms[b.id]?.markAsDirty();
            }
          });
          return [
            AddressTypeEnum.InUseOneUnit,
            AddressTypeEnum.NotInUse,
            AddressTypeEnum.StudentHome,
          ];
        case BuildingTypeEnum.EmptyParcel:
          b.addresses.forEach((a, i) =>
            this.unitForms[b.id]?.patchValue({
              [addressTypeKey + i]: AddressTypeEnum.EmptyParcel,
            })
          );
          return [AddressTypeEnum.EmptyParcel];
        case BuildingTypeEnum.NotInUse:
          b.addresses.forEach((a, i) =>
            this.unitForms[b.id]?.patchValue({
              [addressTypeKey + i]: AddressTypeEnum.NotInUse,
            })
          );
          return [AddressTypeEnum.NotInUse];
        case BuildingTypeEnum.NoAccess:
          b.addresses.forEach((a, i) =>
            this.unitForms[b.id]?.patchValue({
              [addressTypeKey + i]: AddressTypeEnum.InUseOneUnit,
            })
          );
          return [AddressTypeEnum.InUseOneUnit, AddressTypeEnum.NotInUse];
        case BuildingTypeEnum.StudentHome:
          b.addresses.forEach((a, i) =>
            this.unitForms[b.id]?.patchValue({
              [addressTypeKey + i]: AddressTypeEnum.StudentHome,
            })
          );
          return [AddressTypeEnum.StudentHome];
        case BuildingTypeEnum.InUseWithoutBuildingUnit:
          b.addresses.forEach((a, i) => {
            let addressValue = this.unitForms[b.id]?.get([
              addressTypeKey + i,
            ]).value;
            if (
              !(
                addressValue == AddressTypeEnum.InUseZeroUnits ||
                addressValue == AddressTypeEnum.NotInUse
              )
            ) {
              this.unitForms[b.id]?.patchValue({
                [addressTypeKey + i]: AddressTypeEnum.InUseZeroUnits,
              });
              this.unitForms[b.id]?.markAsDirty();
            }
          });
          return [AddressTypeEnum.InUseZeroUnits, AddressTypeEnum.NotInUse];
        default:
          b.addresses.forEach((a, i) => {
            let addressValue = this.unitForms[b.id]?.get([
              addressTypeKey + i,
            ]).value;
            if (
              !(
                addressValue == AddressTypeEnum.InUseOneUnit ||
                addressValue == AddressTypeEnum.NotInUse ||
                addressValue == AddressTypeEnum.StudentHome
              )
            ) {
              this.unitForms[b.id]?.patchValue({
                [addressTypeKey + i]: AddressTypeEnum.InUseOneUnit,
              });
              this.unitForms[b.id]?.markAsDirty();
            }
          });
          return [
            AddressTypeEnum.InUseOneUnit,
            AddressTypeEnum.NotInUse,
            AddressTypeEnum.StudentHome,
          ];
      }
  }

  setAdressTypePossibilities(
    b: Building,
    buildingType: BuildingTypeEnum = b.buildingType
  ) {
    this.addressTypeTexts[b.id] = this.getAdressTypePossibilities(
      b,
      buildingType
    );
  }
}
